import { useCallback, useEffect, useRef, useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { Loader2, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useErrorToast from "~/contexts/error-toast";
import { apiMutate } from "~/utils/apiClient";
import SamplePromptRow from "./SamplePromptRow";

const { VITE_CATALOG_API } = import.meta.env;

const MAX_PROMPTS = 6;

const SamplePromptsTab = () => {
  const { data: dataset, mutate } = useDataset();
  const { showError } = useErrorToast();
  const isOwner = dataset?.access_level === DatasetAccessLevel.OWNER;

  const [prompts, setPrompts] = useState<string[]>(() => dataset?.samplePrompts ?? []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Sync from server when dataset loads/changes externally
  const serverPrompts = dataset?.samplePrompts ?? [];
  const serverKey = JSON.stringify(serverPrompts);
  const [lastServerKey, setLastServerKey] = useState(serverKey);

  if (serverKey !== lastServerKey) {
    setPrompts(serverPrompts);
    setEditingIndex(null);
    setSavingIndex(null);
    setLastServerKey(serverKey);
  }

  const saveAll = useCallback(
    async (toSave: string[]): Promise<boolean> => {
      if (!dataset) return false;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await apiMutate(
          `${VITE_CATALOG_API}/datasets/${dataset.id}/sample-prompts`,
          "PUT",
          { prompts: toSave },
          { signal: controller.signal }
        );

        setLastServerKey(JSON.stringify(toSave));
        mutate((current) => {
          if (!current || current.id !== dataset.id) return current;
          return { ...current, samplePrompts: toSave };
        });
        return true;
      } catch (e) {
        if ((e as Error).name === "AbortError") return false;
        console.error(e);
        showError("Unable to save sample prompts");
        return false;
      }
    },
    [dataset, mutate, showError]
  );

  const handleSave = async (index: number, value: string) => {
    const updated = [...prompts];
    updated[index] = value;
    setSavingIndex(index);

    const success = await saveAll(updated);
    setSavingIndex(null);

    if (success) {
      setPrompts(updated);
      setEditingIndex(null);
    }
  };

  const handleCancel = (index: number) => {
    // Remove newly added empty prompts on cancel
    if (prompts[index] === "") {
      setPrompts((prev) => prev.filter((_, i) => i !== index));
    }
    setEditingIndex(null);
  };

  const handleAdd = () => {
    if (prompts.length >= MAX_PROMPTS) return;
    const updated = [...prompts, ""];
    setPrompts(updated);
    setEditingIndex(updated.length - 1);
  };

  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (deleteIndex === null || !dataset) return;

    const updated = prompts.filter((_, i) => i !== deleteIndex);

    setDeleting(true);
    const success = await saveAll(updated);
    if (success) {
      setPrompts(updated);
      if (editingIndex === deleteIndex) {
        setEditingIndex(null);
      } else if (editingIndex !== null && editingIndex > deleteIndex) {
        setEditingIndex(editingIndex - 1);
      }
    }
    setDeleting(false);
    setDeleteIndex(null);
  };

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Write up to 6 example prompts that demonstrate what users can ask and do with this dataset.
        These will appear at the start of new agent conversations.
      </p>
      <div className="divide-y divide-border">
        {prompts.map((prompt, index) => (
          <SamplePromptRow
            key={`${index}-${editingIndex === index}`}
            index={index}
            value={prompt}
            editing={editingIndex === index}
            saving={savingIndex === index}
            disabled={!isOwner}
            onEdit={() => setEditingIndex(index)}
            onSave={(value) => handleSave(index, value)}
            onCancel={() => handleCancel(index)}
            onDelete={() => setDeleteIndex(index)}
          />
        ))}
      </div>
      {isOwner && prompts.length < MAX_PROMPTS && editingIndex === null && (
        <div>
          <Button variant="secondary" size="sm" onClick={handleAdd}>
            <Plus className="size-4 mr-1" />
            Add Prompt
          </Button>
        </div>
      )}
      <AlertDialog
        open={deleteIndex !== null}
        onOpenChange={(open) => !deleting && !open && setDeleteIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Prompt</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sample prompt?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} onClick={() => setDeleteIndex(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="size-4 mr-1 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SamplePromptsTab;
